import { useCallback, useEffect, useMemo, useState } from 'react'
import { PROFIL_EMAIL, PROFIL_VARIABLE, sanitizeHtml } from 'shared/utils/sanitizeHtml';
import { emailsAPI } from 'services/swagger-api'
import {
  TemplateVariableDto,
  CustomVariableDto,
  EmailRecipientDto
} from 'api/email'

export type EmailConfigVariant = 'standard' | 'lead'

export interface BaseEmailConfig {
  storedFileId: string
  fileName?: string
  sendTo?: EmailRecipientDto[]
  onEmailSent?: () => void
}

export interface StandardEmailConfig extends BaseEmailConfig {
  variant?: 'standard'
  risqueAssureId: string
  fileTypeInt: number
  templateId?: number
}

export interface LeadEmailConfig extends BaseEmailConfig {
  variant: 'lead'
  leadId: string
  templateId: number
}

export type EmailConfig = StandardEmailConfig | LeadEmailConfig

export const isLeadEmailConfig = (config: EmailConfig): config is LeadEmailConfig => {
  return config.variant === 'lead'
}

export const isStandardEmailConfig = (config: EmailConfig): config is StandardEmailConfig => {
  return config.variant !== 'lead'
}

export interface EmailPreview {
  htmlContent: string
  subject: string
  variables: TemplateVariableDto[]
  fileLabel?: string
  /** Copies (CC) ajoutées automatiquement à l'envoi par le back (ex. le courtier). */
  ccRecipients?: { email: string; name?: string | null }[]
}

export interface UseEmailComposerProps {
  emailConfig: StandardEmailConfig
  onClose: () => void
  onBack: () => void
}

/**
 * Génère le HTML du template avec les variables remplacées par des éditeurs rich text
 */
const generateTemplateWithInputs = (
  htmlContent: string,
  variables: TemplateVariableDto[],
  customVariables: Record<string, string>
): string => {
  let html = htmlContent

  // Créer une map des variables avec leur placeholder (nom nettoyé -> variable)
  const variablesMap = new Map<string, TemplateVariableDto>()
  variables.forEach((v) => {
    if (v.name) {
      const cleanName = v.name.replace(/[{}]/g, '').trim()
      variablesMap.set(cleanName, v)
    }
  })

  // Remplacer chaque variable {{ xxx }} ou {{ params.xxx | raw }}
  html = html.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, variableName) => {
    let cleanName = variableName.trim()

    // Gérer le format "params.xxx | raw" -> extraire "xxx"
    const paramsMatch = cleanName.match(/^params\.(\w+)(?:\s*\|\s*raw)?$/)
    if (paramsMatch) {
      cleanName = paramsMatch[1]
    }

    const variable = variablesMap.get(cleanName)

    // Si c'est une variable connue, on la remplace par un éditeur rich text
    if (variable) {
      // Trouver la clé dans customVariables (peut être "{{custom_text}}" ou "custom_text")
      const key = Object.keys(customVariables).find(
        (k) => k.replace(/[{}]/g, '').trim() === cleanName
      )
      const value = key ? customVariables[key] || '' : ''

      // Utiliser le placeholder de l'API ou fallback sur le nom nettoyé
      const placeholder = variable.placeholder || cleanName.replace(/_/g, ' ')

      const escapedPlaceholder = placeholder
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      return `<div
        class="email-variable-editor"
        data-variable-name="${cleanName}"
        style="
          display: block;
          margin: 8px 0;
          border: 2px solid var(--primary-color);
          border-radius: 6px;
          background-color: color-mix(in srgb, var(--primary-color) 5%, white);
          overflow: hidden;
        "
      >
        <div
          class="email-variable-toolbar"
          style="
            display: flex;
            gap: 4px;
            padding: 6px 12px;
            background-color: color-mix(in srgb, var(--primary-color) 10%, white);
            border-bottom: 1px solid color-mix(in srgb, var(--primary-color) 20%, white);
          "
        >
          <button
            type="button"
            class="email-format-btn"
            data-command="bold"
            onmousedown="event.preventDefault(); document.execCommand('bold', false); return false;"
            style="
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 1px solid #d1d5db;
              border-radius: 4px;
              background: white;
              cursor: pointer;
              font-weight: bold;
              font-size: 14px;
              color: #374151;
            "
          >B</button>
          <button
            type="button"
            class="email-format-btn"
            data-command="italic"
            onmousedown="event.preventDefault(); document.execCommand('italic', false); return false;"
            style="
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 1px solid #d1d5db;
              border-radius: 4px;
              background: white;
              cursor: pointer;
              font-style: italic;
              font-size: 14px;
              color: #374151;
            "
          >I</button>
        </div>
        <div
          class="email-variable-content"
          contenteditable="true"
          data-placeholder="${escapedPlaceholder}"
          style="
            min-height: 60px;
            padding: 12px;
            font-family: inherit;
            font-size: inherit;
            line-height: 1.6;
            color: #1f2937;
            outline: none;
          "
        >${value}</div>
      </div>`
    }

    // Sinon on garde la variable telle quelle (non éditable)
    return match
  })

  return html
}

export const useEmailComposer = ({
  emailConfig,
  onClose,
  onBack
}: UseEmailComposerProps) => {
  const [emailPreview, setEmailPreview] = useState<EmailPreview | null>(null)
  const [customVariables, setCustomVariables] = useState<
    Record<string, string>
  >({})
  const [recipients, setRecipients] = useState<string[]>([])
  const [newRecipient, setNewRecipient] = useState('')
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch email template preview
  const fetchEmailPreview = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await emailsAPI.getDocumentTemplatePreview({
        getDocumentTemplatePreviewCommand: {
          risqueAssureId: emailConfig.risqueAssureId,
          fileTypeInt: emailConfig.fileTypeInt,
          templateId: emailConfig.templateId
        }
      })

      if (response.isSuccess && response.htmlContent) {
        setEmailPreview({
          htmlContent: response.htmlContent,
          subject: response.subject || '',
          variables: response.variables || [],
          fileLabel: response.fileLabel,
          ccRecipients: response.ccRecipients ?? undefined
        })

        // Initialize custom variables (empty by default)
        const initialVariables: Record<string, string> = {}
        response.variables?.forEach((v) => {
          if (v.name) {
            initialVariables[v.name] = ''
          }
        })
        setCustomVariables(initialVariables)

        // Initialize recipients from emailConfig.sendTo
        const initialRecipients =
          emailConfig.sendTo
            ?.map((r) => r.email)
            .filter((email): email is string => !!email) || []
        setRecipients(initialRecipients)
        setNewRecipient('')
      } else {
        setError(
          response.errorMessage || 'Erreur lors de la récupération du template'
        )
      }
    } catch {
      setError('Erreur lors de la récupération du template email')
    } finally {
      setIsLoading(false)
    }
  }, [emailConfig])

  useEffect(() => {
    fetchEmailPreview()
  }, [fetchEmailPreview])

  // Handle variable change from inline textarea
  const handleVariableChange = useCallback((variableName: string, value: string) => {
    setCustomVariables((prev) => {
      // Trouver la clé existante qui correspond à ce nom de variable
      const existingKey = Object.keys(prev).find(
        (k) => k.replace(/[{}]/g, '').trim() === variableName
      )
      if (existingKey) {
        return { ...prev, [existingKey]: value }
      }
      // Sinon créer une nouvelle clé avec le format {{variable_name}}
      return { ...prev, [`{{${variableName}}}`]: value }
    })
  }, [])

  // Email validation
  const isValidEmail = useCallback((email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }, [])

  // Add recipient
  const handleAddRecipient = useCallback(() => {
    const email = newRecipient.trim()
    if (email && isValidEmail(email) && !recipients.includes(email)) {
      setRecipients((prev) => [...prev, email])
      setNewRecipient('')
      setShowEmailInput(false)
    }
  }, [newRecipient, recipients, isValidEmail])

  // Handle blur on recipient input - auto validate if valid email
  const handleRecipientBlur = useCallback(() => {
    const email = newRecipient.trim()
    if (email && isValidEmail(email) && !recipients.includes(email)) {
      setRecipients((prev) => [...prev, email])
      setNewRecipient('')
      setShowEmailInput(false)
    } else if (!email) {
      // Si l'input est vide, on le masque
      setShowEmailInput(false)
    }
  }, [newRecipient, recipients, isValidEmail])

  // Show email input
  const handleShowEmailInput = useCallback(() => {
    setShowEmailInput(true)
  }, [])

  // Remove recipient
  const handleRemoveRecipient = useCallback((email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email))
  }, [])

  // Handle Enter key for adding recipient
  const handleRecipientKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAddRecipient()
      }
    },
    [handleAddRecipient]
  )

  // Send email - accepte les variables en paramètre (lues depuis le DOM)
  const handleSendEmail = useCallback(
    async (domVariables?: CustomVariableDto[]) => {
      if (recipients.length === 0) {
        setError('Veuillez ajouter au moins un destinataire')
        return
      }

      setIsSending(true)
      setError(null)

      try {
        // Utiliser les variables du DOM si fournies, sinon fallback sur customVariables
        const customVariablesArray: CustomVariableDto[] =
          domVariables ||
          Object.entries(customVariables).map(([name, value]) => ({
            name,
            value
          }))

        const sendToRecipients: EmailRecipientDto[] = recipients.map(
          (email) => ({ email })
        )

        const response = await emailsAPI.sendDocumentEmail({
          sendDocumentEmailCommand: {
            storedFileId: emailConfig.storedFileId,
            risqueAssureId: emailConfig.risqueAssureId,
            fileTypeInt: emailConfig.fileTypeInt,
            templateId: emailConfig.templateId,
            customVariables:
              customVariablesArray.length > 0
                ? customVariablesArray
                : undefined,
            sendTo: sendToRecipients
          }
        })

        if (response.isSuccess) {
          emailConfig.onEmailSent?.()
          onBack()
          onClose()
        } else {
          setError(response.message || "Erreur lors de l'envoi de l'email")
        }
      } catch {
        setError("Erreur lors de l'envoi de l'email")
      } finally {
        setIsSending(false)
      }
    },
    [emailConfig, customVariables, recipients, onClose, onBack]
  )

  // Get customizable variables
  const customizableVariables = useMemo(() => {
    return emailPreview?.variables || []
  }, [emailPreview])

  // Generate HTML with inline textareas for editable variables
  // Ne dépend PAS de customVariables pour éviter de recréer le HTML à chaque frappe
  const templateHtmlWithInputs = useMemo(() => {
    if (!emailPreview?.htmlContent) return ''
    return generateTemplateWithInputs(
      sanitizeHtml(emailPreview.htmlContent, PROFIL_EMAIL),
      customizableVariables,
      {} // On passe un objet vide, les valeurs seront lues depuis le DOM
    )
  }, [emailPreview?.htmlContent, customizableVariables])

  // Fonction pour collecter les variables depuis le DOM
  const collectVariablesFromDOM = useCallback(
    (containerElement: HTMLElement | null): CustomVariableDto[] => {
      if (!containerElement) return []

      const editors = containerElement.querySelectorAll('.email-variable-editor')
      const variables: CustomVariableDto[] = []

      editors.forEach((editor) => {
        const variableName = (editor as HTMLElement).dataset.variableName
        const contentEl = editor.querySelector('.email-variable-content')
        if (variableName && contentEl) {
          variables.push({
            name: variableName,
            value: sanitizeHtml((contentEl as HTMLElement).innerHTML || '', PROFIL_VARIABLE)
          })
        }
      })

      return variables
    },
    []
  )

  return {
    // State
    emailPreview,
    customVariables,
    recipients,
    newRecipient,
    showEmailInput,
    isLoading,
    isSending,
    error,
    customizableVariables,
    templateHtmlWithInputs,

    // Actions
    setNewRecipient,
    handleVariableChange,
    handleAddRecipient,
    handleRemoveRecipient,
    handleRecipientKeyDown,
    handleRecipientBlur,
    handleShowEmailInput,
    handleSendEmail,
    collectVariablesFromDOM,
    isValidEmail
  }
}
